import { documentNeedsSignature, documentRequestType } from "@/lib/document-request";
import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Download,
  PenLine,
  CheckCircle2,
  Upload,
  FileX,
  XCircle,
  Loader2,
  CalendarDays,
  Pencil,
  RefreshCw,
} from "lucide-react";
import type { HRDocument } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import {
  apiSignHRDocument,
  apiUpdateHRDocumentStatus,
  apiUploadHRDocumentFile,
  apiUpdateHRDocumentContent,
  apiGetHRDocumentFileBlob,
  apiDownloadHRDocumentFile,
  apiGetDocumentAudit,
  getDocumentUploadErrorI18nKey,
  validateDocumentUploadFile,
} from "@/lib/api";
import { DocumentStatusBadge } from "./DocumentStatusBadge";
import { DocumentTypeBadge } from "./DocumentTypeBadge";
import { SignatureModal } from "./SignatureModal";
import { RejectDocumentModal } from "./RejectDocumentModal";
import { DocumentTemplateEditor } from "./DocumentTemplateEditor";
import { toast } from "sonner";

async function renderGeneratedDocumentPdf(content: string): Promise<Blob> {
  const { default: jsPDF } = await import("jspdf");
  const { default: html2canvas } = await import("html2canvas");

  const container = window.document.createElement("div");
  container.style.cssText = [
    "position:fixed",
    "left:-9999px",
    "top:0",
    "width:794px",
    "padding:72px 80px",
    "background:#fff",
    "font-family:Calibri,'Segoe UI',Arial,sans-serif",
    "font-size:12pt",
    "line-height:1.6",
    "color:#000",
  ].join(";");
  container.innerHTML = DOMPurify.sanitize(content);
  window.document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    let yOffset = 0;
    while (yOffset < imgH) {
      if (yOffset > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, -yOffset, imgW, imgH);
      yOffset += pageH;
    }

    return pdf.output("blob");
  } finally {
    container.remove();
  }
}

interface Props {
  doc: HRDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canSign: boolean;
  isHRAdmin: boolean;
  onDocumentUpdated: (doc: HRDocument) => void;
}

// ── EditDocumentContentModal ───────────────────────────────────────────────────

function EditDocumentContentModal({
  doc,
  onClose,
  onSaved,
}: {
  doc: HRDocument;
  onClose: () => void;
  onSaved: (updated: HRDocument) => void;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const handleSave = async (data: { name: string; templateType: any; contentText: string }) => {
    setSaving(true);
    try {
      const updated = await apiUpdateHRDocumentContent(doc.id, data.contentText);
      onSaved(updated);
      toast.success(t("documents.messages.contentUpdated"));
      onClose();
    } catch (err: any) {
      toast.error(t("documents.messages.contentUpdateFailed"), {
        description: err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-full h-[92vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DocumentTemplateEditor
          template={{
            id: doc.id,
            name: doc.documentName,
            templateType: "custom" as any,
            contentText: doc.generatedContent ?? "<p></p>",
            fileKey: null,
            createdBy: null,
            createdByName: null,
            createdAt: "",
            updatedAt: "",
          }}
          onSave={handleSave}
          onCancel={onClose}
          isSaving={saving}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── DocumentViewModal ──────────────────────────────────────────────────────────

export function DocumentViewModal({
  doc,
  open,
  onOpenChange,
  canSign,
  isHRAdmin,
  onDocumentUpdated,
}: Props) {
  const { t } = useTranslation();
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [editContentOpen, setEditContentOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("preview");
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [previewStatus, setPreviewStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [fileDownloading, setFileDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadInProgressRef = useRef(false);

  const docId = doc?.id ?? null;
  const { data: auditData, isLoading: auditLoading } = useQuery({
    queryKey: ["document-audit", docId],
    queryFn: () => apiGetDocumentAudit(docId!),
    enabled: !!docId && activeTab === "history",
  });
  useEffect(() => {
    setSigModalOpen(false);
    setRejectModalOpen(false);
    setEditContentOpen(false);
    setLoading(false);
    setActiveTab("preview");
  }, [docId]);

  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    setPreviewObjectUrl(null);
    if (!open || !docId || !doc?.fileKey) {
      setPreviewStatus("idle");
      return () => {
        active = false;
      };
    }

    setPreviewStatus("loading");
    void apiGetHRDocumentFileBlob(docId)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewObjectUrl(objectUrl);
        setPreviewStatus("ready");
      })
      .catch((error) => {
        console.error("[document preview] authenticated file fetch failed", {
          documentId: docId,
          error,
        });
        if (active) setPreviewStatus("error");
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.fileKey, docId, open, previewAttempt]);

  const handleDownloadPdf = async () => {
    if (!doc?.generatedContent) return;
    setPdfGenerating(true);
    try {
      const pdfBlob = await renderGeneratedDocumentPdf(doc.generatedContent);
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const anchor = window.document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = `${doc.documentName}.pdf`;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    } catch (err: any) {
      toast.error(t("documents.messages.pdfGenerationFailed"), {
        description: err.message,
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  if (!doc) return null;

  const hasSignablePdf = Boolean(
    doc.generatedContent || doc.fileKey?.toLowerCase().endsWith(".pdf"),
  );

  const handleSign = async (signaturePng: string) => {
    try {
      if (!doc.fileKey && doc.generatedContent) {
        const pdfBlob = await renderGeneratedDocumentPdf(doc.generatedContent);
        const safeName = doc.documentName.replace(/[\\/:*?"<>|]+/g, "_");
        const materialized = await apiUploadHRDocumentFile(
          doc.id,
          new File([pdfBlob], `${safeName}.pdf`, { type: "application/pdf" }),
        );
        onDocumentUpdated(materialized);
      }

      const updated = await apiSignHRDocument(doc.id, signaturePng);
      onDocumentUpdated(updated);
      toast.success(t("documents.messages.documentSigned"), {
        description: t("documents.messages.documentSignedDesc", {
          name: doc.documentName,
        }),
      });
    } catch (err: any) {
      toast.error(t("documents.messages.signFailed"), {
        description: err.message,
      });
      throw err;
    }
  };

  const handleMarkCompleted = async () => {
    setLoading(true);
    try {
      const updated = await apiUpdateHRDocumentStatus(doc.id, "Completed");
      onDocumentUpdated(updated);
      toast.success(t("documents.messages.documentCompleted"), {
        description: t("documents.messages.documentCompletedDesc", {
          name: doc.documentName,
        }),
      });
      onOpenChange(false);
    } catch (err: any) {
      toast.error(t("documents.messages.statusUpdateFailed"), {
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFile = async (file: File) => {
    if (uploadInProgressRef.current) return;
    uploadInProgressRef.current = true;
    setLoading(true);
    try {
      const updated = await apiUploadHRDocumentFile(doc.id, file);
      onDocumentUpdated(updated);
      toast.success(t("documents.messages.fileUploaded"), {
        description: t("documents.messages.fileUploadedDesc", {
          name: file.name,
        }),
      });
    } catch (err: any) {
      const translationKey = getDocumentUploadErrorI18nKey(err);
      toast.error(t("documents.messages.uploadFailed"), {
        description: translationKey ? t(translationKey) : err.message,
      });
    } finally {
      uploadInProgressRef.current = false;
      setLoading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validationCode = validateDocumentUploadFile(file);
      if (validationCode) {
        const translationKey = getDocumentUploadErrorI18nKey({ code: validationCode });
        toast.error(t("documents.messages.uploadFailed"), {
          description: translationKey ? t(translationKey) : undefined,
        });
        e.target.value = "";
        return;
      }
      handleUploadFile(file);
      e.target.value = "";
    }
  };

  const handleDownloadStoredFile = async () => {
    if (!doc.fileKey) return;
    setFileDownloading(true);
    try {
      if (previewObjectUrl) {
        const fileExtension = doc.fileKey.match(/\.[a-z0-9]+$/i)?.[0] ?? "";
        const hasExtension = /\.[a-z0-9]+$/i.test(doc.documentName);
        const anchor = window.document.createElement("a");
        anchor.href = previewObjectUrl;
        anchor.download = `${doc.documentName}${hasExtension ? "" : fileExtension}`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } else {
        await apiDownloadHRDocumentFile(doc.id, doc.documentName);
      }
    } catch (error) {
      console.error("[document download] authenticated file fetch failed", {
        documentId: doc.id,
        error,
      });
      toast.error(t("documents.previewError.downloadFailed"));
    } finally {
      setFileDownloading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[720px] p-0 gap-0 flex flex-col max-h-[90vh] overflow-hidden">
          <div className="px-6 pt-6 pb-4 bg-muted/30 border-b shrink-0">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 pr-8">
                <DialogTitle className="flex items-center gap-2.5 text-base font-semibold leading-tight min-w-0">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <span className="truncate">{doc.documentName}</span>
                </DialogTitle>
                <div className="flex items-center gap-2 shrink-0">
                  {doc.documentType && <DocumentTypeBadge type={doc.documentType} />}
                  <Badge variant="outline">{t(`documents.requestKind.${documentRequestType(doc)}`)}</Badge>
                  <DocumentStatusBadge status={doc.status} />
                </div>
              </div>
            </DialogHeader>
            {doc.requestedByName && (
              <p className="text-xs text-muted-foreground mt-2">
                {t("documents.requestedBy")}: {doc.requestedByName}
              </p>
            )}
          </div>

          <div className="px-6 py-5 flex-1 overflow-y-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="preview">{t("documents.preview")}</TabsTrigger>
                <TabsTrigger value="history">{t("documents.historyAudit")}</TabsTrigger>
              </TabsList>
              
              <TabsContent value="preview" className="mt-0">
                <div className="h-[380px] rounded-xl border bg-muted/30 overflow-hidden flex items-center justify-center">
                  {doc.fileKey && previewStatus === "loading" ? (
                    <div className="flex flex-col items-center gap-3 text-center px-6" role="status">
                      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {t("documents.previewLoading")}
                      </p>
                    </div>
                  ) : doc.fileKey && previewStatus === "error" ? (
                    <div className="flex flex-col items-center gap-3 text-center px-6" role="alert">
                      <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
                        <FileX className="h-7 w-7 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {t("documents.previewError.title")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("documents.previewError.description")}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPreviewAttempt((attempt) => attempt + 1)}
                        >
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                          {t("documents.previewError.retry")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadStoredFile}
                          disabled={fileDownloading}
                        >
                          {fileDownloading ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          {t("documents.download")}
                        </Button>
                      </div>
                    </div>
                  ) : previewObjectUrl ? (
                    <iframe
                      src={previewObjectUrl}
                      title={doc.documentName}
                      className="w-full h-full border-0"
                    />
                  ) : doc.generatedContent ? (
                    <div className="w-full h-full overflow-y-auto bg-white dark:bg-zinc-950">
                      <div
                        className="px-8 py-6 prose prose-sm max-w-none dark:prose-invert"
                        style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif", fontSize: "12pt" }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.generatedContent) }}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3 text-center px-6">
                      <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
                        <FileX className="h-7 w-7 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {t("documents.noFile")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("documents.noFileDesc")}
                        </p>
                      </div>
                      {(canSign || isHRAdmin) && doc.status !== "Rejected" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={loading}
                        >
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          {t("documents.uploadFile")}
                        </Button>
                      )}

                    </div>
                  )}
                </div>

                {doc.status === "Rejected" && doc.rejectionReason && (
                  <div className="mt-3 px-3 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                    <span className="font-medium">{t("documents.rejectionReason")}: </span>
                    {doc.rejectionReason}
                  </div>
                )}

                {doc.documentExpiry && (
                  <div className="mt-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-400 flex items-center gap-2">
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      {t("documents.documentExpires")}:{" "}
                      <span className="font-medium">
                        {new Date(doc.documentExpiry).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </span>
                  </div>
                )}

                {doc.gdprExpired && (
                  <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400">
                    {t("documents.gdprExpired")}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="history" className="mt-0">
                <div className="h-[380px] rounded-xl border bg-muted/10 overflow-y-auto p-4">
                  {auditLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {auditData?.versions && auditData.versions.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-3">{t("documents.versionHistory")}</h4>
                          <div className="space-y-2">
                            {auditData.versions.map((v) => (
                              <div key={v.id} className="flex items-center justify-between p-2 rounded-md bg-muted/50 border text-sm">
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline">v{v.versionNumber}</Badge>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{v.uploadedByName || t("documents.system")}</span>
                                    <span className="text-xs text-muted-foreground">{(v.fileSize / 1024).toFixed(1)} KB</span>
                                  </div>
                                </div>
                                <span className="text-xs text-muted-foreground">{format(new Date(v.createdAt), "MMM d, yyyy HH:mm")}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {auditData?.audit && auditData.audit.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold mb-3">{t("documents.auditLog")}</h4>
                          <div className="relative border-l ml-3 space-y-4">
                            {auditData.audit.map((log) => (
                              <div key={log.id} className="relative pl-6">
                                <div className="absolute w-2 h-2 bg-primary rounded-full -left-[5px] top-1.5 ring-4 ring-background" />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">
                                    {t(
                                      `documents.auditActions.${
                                        log.metadata?.operation === "document_signed"
                                          ? "document_signed"
                                          : log.actionType
                                      }`,
                                      log.actionType,
                                    )}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {t("documents.auditEntry", {
                                      user: log.userName || t("documents.system"),
                                      role: log.role
                                        ? t(`documents.auditRoles.${log.role}`, log.role)
                                        : t("documents.userRole"),
                                      date: format(new Date(log.createdAt), "MMM d, yyyy HH:mm"),
                                    })}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!auditData?.versions?.length && !auditData?.audit?.length && (
                        <div className="text-center py-8 text-sm text-muted-foreground">
                          {t("documents.noHistory")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="px-6 pb-5 pt-3 border-t bg-muted/20 flex items-center justify-end gap-2 shrink-0">
            {doc.fileKey && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadStoredFile}
                disabled={fileDownloading || previewStatus === "loading"}
              >
                {fileDownloading ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                )}
                {t("documents.download")}
              </Button>
            )}
            {!doc.fileKey && doc.generatedContent && (
              <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfGenerating}>
                {pdfGenerating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                {t("documents.download")}
              </Button>
            )}
            {isHRAdmin && doc.generatedContent && doc.status !== "Completed" && doc.status !== "Rejected" && (
              <Button variant="outline" size="sm" onClick={() => setEditContentOpen(true)} disabled={loading}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                {t("common.edit")}
              </Button>
            )}
            {isHRAdmin && doc.status !== "Completed" && doc.status !== "Rejected" && (
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {t("documents.upload")}
              </Button>
            )}
            {canSign && !isHRAdmin && doc.status === "Pending" && (
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={loading}>
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                {t("documents.upload")}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={loading}>
              {t("documents.close")}
            </Button>
            {canSign && documentNeedsSignature(doc) && doc.status === "Pending" && hasSignablePdf && (
              <Button
                size="sm"
                onClick={() => setSigModalOpen(true)}
                disabled={loading}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
              >
                <PenLine className="h-3.5 w-3.5 mr-1.5" />
                {t("documents.signDocument")}
              </Button>
            )}
            {isHRAdmin && (doc.status === "Pending" || doc.status === "Signed") && (
              <Button size="sm" variant="destructive" onClick={() => setRejectModalOpen(true)} disabled={loading}>
                <XCircle className="h-3.5 w-3.5 mr-1.5" />
                {t("documents.reject")}
              </Button>
            )}
            {isHRAdmin && (doc.status === "Pending" || doc.status === "Signed") && (
              <Button
                size="sm"
                onClick={handleMarkCompleted}
                disabled={loading}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 border-0"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                {t("documents.markCompleted")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        className="hidden"
        onChange={handleFileInputChange}
        disabled={loading}
      />

      <SignatureModal
        open={sigModalOpen}
        onOpenChange={setSigModalOpen}
        onSign={handleSign}
      />

      <RejectDocumentModal
        doc={doc}
        open={rejectModalOpen}
        onOpenChange={setRejectModalOpen}
        onDocumentUpdated={(updated) => {
          onDocumentUpdated(updated);
          onOpenChange(false);
        }}
      />

      {editContentOpen && (
        <EditDocumentContentModal
          doc={doc}
          onClose={() => setEditContentOpen(false)}
          onSaved={(updated) => {
            onDocumentUpdated(updated);
            setEditContentOpen(false);
          }}
        />
      )}
    </>
  );
}
